/**
 * Authentication API service
 */

import { api } from '@services/api';
import { API_PATHS } from '@utils/constants';
import type {
  AuthStatus,
  LoginUrlResponse,
  LogoutResponse,
  TokenResponse,
  User,
} from '../types/auth.types';

export const authApi = {
  /**
   * Get current authentication status
   */
  getStatus: async (): Promise<AuthStatus> => {
    const response = await api.get<AuthStatus>(`${API_PATHS.AUTH}/status`);
    return response.data;
  },

  /**
   * Get OAuth2 login URL with PKCE parameters
   */
  getLoginUrl: async (): Promise<LoginUrlResponse> => {
    const response = await api.get<LoginUrlResponse>(`${API_PATHS.AUTH}/login`);
    return response.data;
  },

  /**
   * Exchange authorization code for tokens
   */
  exchangeCode: async (
    code: string,
    state: string,
    codeVerifier: string
  ): Promise<TokenResponse> => {
    const response = await api.get<TokenResponse>(`${API_PATHS.AUTH}/callback`, {
      params: { code, state, code_verifier: codeVerifier },
    });
    return response.data;
  },

  /**
   * Refresh access token using refresh token
   */
  refreshToken: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await api.post<TokenResponse>(`${API_PATHS.AUTH}/refresh`, {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  /**
   * Get current user info
   */
  getUserInfo: async (): Promise<User> => {
    const response = await api.get<User>(`${API_PATHS.AUTH}/userinfo`);
    return response.data;
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<LogoutResponse> => {
    const response = await api.post<LogoutResponse>(`${API_PATHS.AUTH}/logout`);
    return response.data;
  },
};
